import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Container, Title, Text, Paper, Group, ThemeIcon, Box, SimpleGrid, SegmentedControl } from '@mantine/core';
import { Target, TreeStructure, CurrencyDollar, Globe, Minus, Users, ArrowRight, Plugs, MagnifyingGlass, Image, UsersThree, GoogleLogo, MetaLogo } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import Footer from '../components/Footer';

const GOOGLE_GUIDES = [
  {
    labelKey: 'firstSteps',
    guides: [
      { id: 'google-ads-setup', icon: Plugs, color: 'brand' },
      { id: 'conversions', icon: Target, color: 'green' },
      { id: 'campaign-structure', icon: TreeStructure, color: 'blue' },
      { id: 'keyword-match-types', icon: MagnifyingGlass, color: 'violet' },
    ],
  },
  {
    labelKey: 'optimization',
    guides: [
      { id: 'bidding', icon: CurrencyDollar, color: 'orange' },
      { id: 'negative-keywords', icon: Minus, color: 'red' },
      { id: 'landing-pages', icon: Globe, color: 'brand' },
    ],
  },
  {
    labelKey: 'advanced',
    guides: [
      { id: 'remarketing', icon: Users, color: 'violet' },
    ],
  },
];

const META_GUIDES = [
  {
    labelKey: 'firstSteps',
    guides: [
      { id: 'meta-ads-setup', icon: Plugs, color: 'indigo' },
    ],
  },
  {
    labelKey: 'targeting',
    guides: [
      { id: 'meta-audience', icon: UsersThree, color: 'blue' },
    ],
  },
  {
    labelKey: 'creatives',
    guides: [
      { id: 'meta-creatives', icon: Image, color: 'green' },
    ],
  },
];

function GuideCard({ guide }) {
  const { t } = useTranslation();
  const Icon = guide.icon;
  return (
    <Paper
      component={Link}
      to={`/poradniki/${guide.id}`}
      withBorder p="md" radius="md"
      style={{ textDecoration: 'none', color: 'inherit', transition: 'box-shadow 0.15s, transform 0.15s' }}
      className="campaign-row"
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <ThemeIcon variant="light" color={guide.color} size="lg" radius="md">
          <Icon size={20} weight="duotone" />
        </ThemeIcon>
        <Box style={{ flex: 1 }}>
          <Text size="sm" fw={600} mb={2}>{t(`guidesPublic.guides.${guide.id}.title`)}</Text>
          <Text size="xs" c="dimmed" lh={1.5}>{t(`guidesPublic.guides.${guide.id}.description`)}</Text>
        </Box>
        <ArrowRight size={14} className="text-gray-400" style={{ flexShrink: 0, marginTop: 4 }} />
      </Group>
    </Paper>
  );
}

export default function PoradnikiPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialPlatform = searchParams.get('platform') || 'all';
  const [platform, setPlatform] = useState(initialPlatform);

  const showGoogle = platform === 'all' || platform === 'google';
  const showMeta = platform === 'all' || platform === 'meta';

  return (
    <>
      <SEO
        title={t('guidesPublic.seoTitle')}
        description={t('guidesPublic.seoDescription')}
        path="/poradniki"
      />
      <Navbar />
      <Container size="md" py={48}>
        <Title order={2} ta="center" mb={8}>{t('guidesPublic.heading')}</Title>
        <Text ta="center" c="dimmed" mb="lg" maw={500} mx="auto">
          {t('guidesPublic.subtitle')}
        </Text>

        {/* Platform selector */}
        <Group justify="center" mb="xl">
          <SegmentedControl
            value={platform}
            onChange={setPlatform}
            data={[
              { value: 'all', label: t('guidesPublic.filterAll') },
              {
                value: 'google',
                label: (
                  <Group gap={6} wrap="nowrap">
                    <GoogleLogo size={14} weight="bold" style={{ color: '#4285F4' }} />
                    <span>Google Ads</span>
                  </Group>
                ),
              },
              {
                value: 'meta',
                label: (
                  <Group gap={6} wrap="nowrap">
                    <MetaLogo size={14} weight="bold" style={{ color: '#1877F2' }} />
                    <span>Meta Ads</span>
                  </Group>
                ),
              },
            ]}
            size="sm"
          />
        </Group>

        {/* Google Ads guides */}
        {showGoogle && GOOGLE_GUIDES.map(group => (
          <Box key={group.labelKey} mb="xl">
            <Group gap="xs" mb="sm">
              {platform === 'all' && <GoogleLogo size={14} weight="bold" style={{ color: '#4285F4' }} />}
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
                {platform === 'all' ? `Google Ads / ${t(`guidesPublic.groups.${group.labelKey}`)}` : t(`guidesPublic.groups.${group.labelKey}`)}
              </Text>
            </Group>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
              {group.guides.map(guide => <GuideCard key={guide.id} guide={guide} />)}
            </SimpleGrid>
          </Box>
        ))}

        {/* Meta Ads guides */}
        {showMeta && META_GUIDES.map(group => (
          <Box key={group.labelKey} mb="xl">
            <Group gap="xs" mb="sm">
              {platform === 'all' && <MetaLogo size={14} weight="bold" style={{ color: '#1877F2' }} />}
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
                {platform === 'all' ? `Meta Ads / ${t(`guidesPublic.groups.${group.labelKey}`)}` : t(`guidesPublic.groups.${group.labelKey}`)}
              </Text>
            </Group>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
              {group.guides.map(guide => <GuideCard key={guide.id} guide={guide} />)}
            </SimpleGrid>
          </Box>
        ))}
      </Container>
      <Footer />
    </>
  );
}
